import React, {useState, useMemo, useRef} from 'react';
import { withStyles, Typography, Tooltip, IconButton } from '@material-ui/core';
import { connect } from 'react-redux';
import PlanList from './PlansList';
import { hasPermission, formatDate } from '../../../../sc-utilities';
import { CustomChip } from '../../../../sc-components';
import { Info, Delete } from '@material-ui/icons';
import InfoDialog from './InfoDialog';
import PlanDelete from "./PlansDelete";


const styles = theme => ({
    root: {},
    paper: {},
    logo: {},
    tableCell: {
        paddingLeft: 15
    },
    "@global": {
        "[class*='chipActive']": {
            color: theme.palette.success.dark
        },
        "[class*='chipPrepared']": {
            color: theme.palette.warning.dark
        },
        "[class*='chipInactive']": {
            color: theme.palette.error.dark
        },
    },
    chipActive: {
        color: theme.palette.success.dark,
    },
    chipPrepared: {
        color: theme.palette.warning.dark,
    },
    chipInactive: {
        color: theme.palette.error.dark,
    },
    tableDiv: {
        paddingBottom: theme.spacing(2)
    }
});

export const openInfo = (Plan, setInfoPlans, setOpen, translations) => {
    const plan = Plan?.Configuration ? JSON.parse(Plan.Configuration) : {};
    const configuration = [];

    for (var key in plan) {
        if (plan.hasOwnProperty(key)) {
            const element = plan[key];
            switch (key) {
                case 'MonthlyPrice':
                    configuration.push(`${translations["monthly_price"] || 'Monthly price'}: ${element}`);
                    break;
                case 'MaxAllowedUsers':
                    configuration.push(`${translations["max_allowed_users"] || 'Max allowed users'}: ${element} `);
                    break;
                case 'AllowedOveruse':
                    configuration.push(`${translations["allowed_users"] || 'Allowed overuse'}: ${element ? element : 'unlimited'} `);
                    break;
                case 'MaxRemoteDiskNumber':
                    configuration.push(`${translations["max_remote_disk_number"] || 'Max remote disk number'}: ${element} `);
                    break;
                case 'ErasureRedemptionPeriod':
                    configuration.push(`${translations["erasure_redemption_period"] || 'Erasure redemption period'}: ${element} `);
                    break;
                case 'DiagnoseRedemptionPeriod':
                    configuration.push(`${translations["diagnose_redemption_period"] || 'Diagnose redemption period'}: ${element} `);
                    break;
                case 'VerificationRedemptionPeriod':
                    configuration.push(`${translations["verification_redemption_period"] || 'Erasure verification redemption period'}: ${element} `);
                    break;
                case 'CheckRedemptionPeriod':
                    configuration.push(`${translations["asset_check_redemption_period"] || 'Asset check redemption period'}: ${element} `);
                    break;
                case 'IncludedUsage':
                    configuration.push(`${translations["included_usage"] || 'Included usage'}: ${element}`);
                    break;
                case 'MinimalUsage':
                    configuration.push(`${translations["minimal_usage"] || 'Minimal usage'}: ${element} `);
                    break;
                default:

                    break;
            }
        }
    }

    setInfoPlans({
        data: configuration,
        description: Plan?.Description,
        name: Plan?.Name,
        priceList: Plan?.PriceList ? JSON.parse(Plan.PriceList) : {},
        min: plan?.MinimalUsage ? plan?.MinimalUsage : 0,
        max: plan?.IncludedUsage ? plan?.IncludedUsage + (plan?.AllowedOveruse ? plan.AllowedOveruse : 0) : 0
    })
    setOpen(true);
};

const Plan = props => {
    const tableRef = useRef();
    const { activeCustomerID, classes, translations, permissions, columns, extraFilterParams, onAccountPlansList, onListPlans, billingAccount, onAssignPlans, removePlans, onLoadAccount } = props;
    let visible = columns && columns['Plan'];
    const [infoPlans, setInfoPlans] = useState({ data: [], description: '', name: '', priceList: [], min: 0, max: 0 })
    const [open, setOpen] = useState(false);

    const [deleteAccountPlanModalState, setDeleteAccountPlanModalState] = useState({
        open: false,
        PlanID: null
    })

    const handleClose = () => setOpen(false);



    const getFormattedStatus = (id, status) => {
        switch (status) {
            case 'active':
                return <CustomChip key={`${id}`} className={classes['chipActive']} value={translations['active'] || 'Active'} />;
            case 'inactive':
                return <CustomChip key={`${id}`} className={classes['chipInactive']} value={translations['inactive'] || 'Inactive'} />;
            case 'prepared':
                return <CustomChip key={`${id}`} className={classes['chipPrepared']} value={translations['prepared'] || 'Prepared'} />;
            default:
                return '';
        }
    };

    const handleDelete = (event, rowData) => {
        if (!rowData) {
            setDeleteAccountPlanModalState({ open: false, PlanID: null });
            return;
        }
        setDeleteAccountPlanModalState({ open: true, PlanID: rowData.PlanID, rowData: rowData.Plan });
    };

    let tableColumns = useMemo(() => {
        return [
            {
                title: translations["action"] || "Action", field: 'Action',
                hidden: visible ? visible['Action'] : false,
                cellStyle: { minWidth: 100, maxWidth: 100 },
                filtering: false,
                render: rowData => <>
                    {hasPermission(permissions, 'plan_list') ?
                        <Tooltip title={translations["plan_info"] || "Plan info"}>
                            <IconButton value={rowData.PlanID} onClick={() => openInfo(rowData?.Plan, setInfoPlans, setOpen, translations)} aria-label={translations["plan_info"] || "Plan info"}>
                                <Info />
                            </IconButton>
                        </Tooltip> : null
                    }
                    {hasPermission(permissions, 'billing_account_plan_delete') ?
                        <Tooltip key={`Delete-${rowData.PlanID}`} title={translations[""] || ""}>
                            <IconButton
                                id={`delete_billing_plan_${rowData.PlanID}`}
                                component='div'
                                onClick={(event) => handleDelete(event, rowData)}
                            >
                                <Delete />
                            </IconButton>
                        </Tooltip>
                        : null
                    }
                </>
            },
            {
                title: translations["name"] || 'Name', field: 'Name',
                hidden: visible ? visible['Name'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Plan && rowData.Plan.Name ? rowData.Plan.Name : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["description"] || 'Description', field: 'Description',
                hidden: visible ? visible['Description'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Plan && rowData.Plan.Description ? rowData.Plan.Description : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["status"] || 'Status', field: 'Status',
                hidden: visible ? visible['Status'] : false,
                cellStyle: { minWidth: 100, maxWidth: 100 },
                lookup: { 'active': translations['active'] || 'Active', 'prepared': translations['prepared'] || 'Prepared', 'inactive': translations['inactive'] || 'Inactive' },
                defaultFilter: ['active', 'prepared'],
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Plan && rowData.Plan.Status ? getFormattedStatus(rowData.PlanID, rowData.Plan.Status) : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["valid_to"] || 'Valid to', field: 'ValidTo',
                hidden: visible ? visible['ValidTo'] : false,
                type: 'date',
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Plan && rowData.Plan.ValidTo ? formatDate(rowData.Plan.ValidTo) : translations['date_not_set'] || 'Expiration date not set'}</Typography>;
                }
            },
            {
                title: translations["deleted"] || 'Deleted', field: 'Deleted',
                hidden: visible ? visible['Deleted'] : false,
                lookup: { '0': translations['no'] || 'No', '1': translations['yes'] || 'Yes' },
                defaultFilter: ['0'],
                filtering: true,
                width: 100,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Deleted ? translations['yes'] || 'Yes' : translations['no'] || 'No'}</Typography>;
                }
            }
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    const handleCloseDanger = async () => {
        if (deleteAccountPlanModalState.open) {
            await removePlans({ ActiveAccountID: extraFilterParams.ActiveAccountID, PlanIDs: [deleteAccountPlanModalState.PlanID], ActiveCustomerID: activeCustomerID });
            handleCloseSafe();
            tableRef.current && tableRef.current.onQueryChange();
        }
    }

    const handleCloseSafe = () => {
        setDeleteAccountPlanModalState({
            open: false,
            PlanID: null
        })
    }

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'billing_account_plan_list') ?
                <PlanList
                    tableRef={tableRef}
                    activeCustomerID={activeCustomerID}
                    translations={translations}
                    permissions={permissions}
                    tableColumns={tableColumns}
                    onAccountPlansList={onAccountPlansList}
                    extraFilterParams={extraFilterParams}
                    onListPlans={onListPlans}
                    billingAccount={billingAccount}
                    assignPlans={onAssignPlans}
                    removePlans={removePlans}
                    onLoadAccount={onLoadAccount}
                />
                : null}
            {hasPermission(permissions, 'billing_account_plan_delete') ? (
                <PlanDelete
                    entityName={translations['account_plan'] || 'Account plan'}
                    translations={translations}
                    handleCloseDanger={handleCloseDanger}
                    handleCloseSafe={handleCloseSafe}
                    deleteModalState={deleteAccountPlanModalState}
                    deleteOne={true}
                />
            ) : null}
            <InfoDialog open={open} infoPlans={infoPlans} translations={translations} handleClose={handleClose} />
        </div >
    );
}

const mapStateToProps = state => {
    return {
        billingAccount: state.billing.accounts,
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
    };
};

export default connect(
    mapStateToProps,
    null,
)(withStyles(styles, { withTheme: true })(Plan));